import { z } from "zod";
import { publicMemberTagsSchema } from "./memberTags";

// ---------------------------------------------------------------------------
// Product-commerce staff permissions
// ---------------------------------------------------------------------------

/**
 * ⚠️ **All six keys are INERT for authorization since Sep 2026.** They are
 * retained for wire/persistence compatibility only — the API still accepts and
 * stores them, and nothing reads them. Product capability now resolves in two
 * places, neither of which is a per-member column:
 *
 * - **Universal for any active member** (staff portal): selling and per-line
 *   discounts. A business that never enabled product sales is stopped by
 *   `resolveCommerceEnablement`, not by a permission.
 * - **Dashboard section grants** (owner, or manager with the grant): catalog
 *   administration (`products`), cost visibility (`product-costs`), inventory
 *   (`product-inventory`), and seller attribution (`product-sales`). Every one
 *   of these returns false for a staff session regardless of role — the staff
 *   portal sells as the signed-in member and never edits the catalog.
 *
 * See `staffCan` / `dashboardCan` in
 * `apps/api/src/modules/commerce/access.ts`, which is the only place these
 * rules live. Do not reintroduce a read of these keys; add a dashboard
 * capability or widen the universal set there instead.
 */
export const PRODUCT_COMMERCE_STAFF_PERMISSION_KEYS = [
  "canSellProducts",
  "canManageProducts",
  "canAdjustProductInventory",
  "canDiscountProductSales",
  "canAssignProductSeller",
  "canViewProductCosts",
] as const;

export type ProductCommerceStaffPermissionKey =
  (typeof PRODUCT_COMMERCE_STAFF_PERMISSION_KEYS)[number];

const productCommerceStaffPermissionShape = {
  canSellProducts: z.boolean(),
  canManageProducts: z.boolean(),
  canAdjustProductInventory: z.boolean(),
  canDiscountProductSales: z.boolean(),
  canAssignProductSeller: z.boolean(),
  canViewProductCosts: z.boolean(),
} satisfies Record<ProductCommerceStaffPermissionKey, z.ZodBoolean>;

/**
 * A complete product-commerce staff permission snapshot. Fields stay required
 * so a future serializer/client mismatch fails visibly instead of silently
 * granting or inventing a value.
 */
export const productCommerceStaffPermissionsSchema = z
  .object(productCommerceStaffPermissionShape)
  .strict();

export type ProductCommerceStaffPermissions = z.infer<
  typeof productCommerceStaffPermissionsSchema
>;

/**
 * Fail-closed initialization for new consumers. Database defaults remain the
 * responsibility of the Stage-0-gated TeamMember migration.
 */
export const DEFAULT_PRODUCT_COMMERCE_STAFF_PERMISSIONS = Object.freeze({
  canSellProducts: false,
  canManageProducts: false,
  canAdjustProductInventory: false,
  canDiscountProductSales: false,
  canAssignProductSeller: false,
  canViewProductCosts: false,
} satisfies ProductCommerceStaffPermissions);

// ---------------------------------------------------------------------------
// Stripe Connect summary (mirrors the dashboard's StripeAccountSummary).
// Used by both owner-side preview of a member's Stripe status and the
// staff-portal's own `/v1/staff/me/stripe-account` endpoint.
// ---------------------------------------------------------------------------

export const stripeAccountStatusSchema = z.enum([
  "NOT_CONNECTED",
  "ONBOARDING",
  "ENABLED",
]);
export type StripeAccountStatus = z.infer<typeof stripeAccountStatusSchema>;

export const teamMemberStripeAccountSummarySchema = z.object({
  accountId: z.string().nullable(),
  status: stripeAccountStatusSchema,
  detailsSubmitted: z.boolean(),
  chargesEnabled: z.boolean(),
  payoutsEnabled: z.boolean(),
  hasPendingRequirements: z.boolean(),
  requirementsDue: z.array(z.string()),
  futureRequirements: z.array(z.string()),
  disabledReason: z.string().nullable(),
  disabledReasonCode: z.string().nullable(),
  country: z.string().nullable(),
  defaultCurrency: z.string().nullable(),
  businessType: z.string().nullable(),
  supportEmail: z.string().nullable(),
  supportPhone: z.string().nullable(),
  lastSync: z.string(),
});
export type TeamMemberStripeAccountSummary = z.infer<
  typeof teamMemberStripeAccountSummarySchema
>;

// ---------------------------------------------------------------------------
// Payment split settings
// ---------------------------------------------------------------------------

export const paymentSplitTypeSchema = z.enum(["NONE", "PERCENT", "FLAT"]);
export type PaymentSplitType = z.infer<typeof paymentSplitTypeSchema>;

export const paymentFeePayerSchema = z.enum(["BUSINESS", "STAFF"]);
export type PaymentFeePayer = z.infer<typeof paymentFeePayerSchema>;

/**
 * Worker classification label (EMPLOYEE = W-2, CONTRACTOR = 1099). Display
 * only: it does not affect payment routing, ledger splits, or tax forms. Money
 * follows `servicePaymentSplitType`, the fee payers, `tipSplitEnabled`, and
 * cash deduction (see `computeRouting` / `allocateCheckoutLedger` in the API
 * payments module).
 */
export const salonMemberWorkTypeSchema = z.enum(["EMPLOYEE", "CONTRACTOR"]);
export type SalonMemberWorkType = z.infer<typeof salonMemberWorkTypeSchema>;

/**
 * GET response shape for owner-side and staff-side payment-settings reads.
 * Excludes `stripeAccountId` / `stripeAccountStatus` — Stripe details flow
 * through the dedicated `*StripeAccountSummary*` endpoints.
 */
export const teamMemberPaymentSettingsSchema = z.object({
  servicePaymentSplitType: paymentSplitTypeSchema,
  servicePaymentSplitValue: z.number().nullable(),
  serviceSplitFeePayer: paymentFeePayerSchema,
  tipSplitEnabled: z.boolean(),
  tipSplitFeePayer: paymentFeePayerSchema,
  /**
   * When true, cash collected by staff is recorded as held by the staff and
   * the business's commission share is owed (settled by deducting from the
   * staff's next online/Stripe payout). UI gates this on the staff having
   * a connected Stripe account.
   */
  cashDeductionEnabled: z.boolean(),
  workType: salonMemberWorkTypeSchema.nullable(),
});
export type TeamMemberPaymentSettings = z.infer<
  typeof teamMemberPaymentSettingsSchema
>;

/**
 * PUT body. Server enforces:
 * - PERCENT → value in [0, 100]
 * - FLAT    → value > 0 AND ≤ smallest non-zero priced service the member is
 *             assigned to (when one exists).
 * - NONE    → value ignored / set null.
 */
export const updateTeamMemberPaymentSettingsSchema = z
  .object({
    servicePaymentSplitType: paymentSplitTypeSchema,
    servicePaymentSplitValue: z.number().nullable().optional(),
    serviceSplitFeePayer: paymentFeePayerSchema,
    tipSplitEnabled: z.boolean(),
    tipSplitFeePayer: paymentFeePayerSchema,
    cashDeductionEnabled: z.boolean().optional(),
  })
  .strict();
export type UpdateTeamMemberPaymentSettingsRequest = z.infer<
  typeof updateTeamMemberPaymentSettingsSchema
>;

// Response wrappers (the API uses `{ ok: true, data: ... }` envelopes — these
// are the inner payloads only).
export const getTeamMemberPaymentSettingsResponseSchema = z.object({
  settings: teamMemberPaymentSettingsSchema,
});
export type GetTeamMemberPaymentSettingsResponse = z.infer<
  typeof getTeamMemberPaymentSettingsResponseSchema
>;

export const updateTeamMemberPaymentSettingsResponseSchema =
  getTeamMemberPaymentSettingsResponseSchema;
export type UpdateTeamMemberPaymentSettingsResponse =
  GetTeamMemberPaymentSettingsResponse;

// ---------------------------------------------------------------------------
// Staff Stripe account endpoints
// ---------------------------------------------------------------------------

/**
 * Current platform payment-terms version (docs/architecture/
 * payment-legal-structure.md). Matches `legal.terms.lastUpdated` in the
 * landing locales; bump both together.
 */
export const PAYMENT_TERMS_VERSION = "2026-09-25";

export const staffStripeAccountResponseSchema = z.object({
  summary: teamMemberStripeAccountSummarySchema,
  /** Whether the member accepted `PAYMENT_TERMS_VERSION`. */
  paymentTermsAccepted: z.boolean().optional(),
});
export type StaffStripeAccountResponse = z.infer<
  typeof staffStripeAccountResponseSchema
>;

export const staffStripeOnboardingLinkRequestSchema = z
  .object({
    returnPath: z.string().optional(),
    refreshPath: z.string().optional(),
    /**
     * Records acceptance of `PAYMENT_TERMS_VERSION`. Required unless the
     * member already accepted it; the API answers 422 otherwise.
     */
    acceptPaymentTerms: z.literal(true).optional(),
  })
  .strict();
export type StaffStripeOnboardingLinkRequest = z.infer<
  typeof staffStripeOnboardingLinkRequestSchema
>;

export const staffPaymentTermsAcceptResponseSchema = z.object({
  termsVersion: z.string(),
  acceptedAt: z.string(),
});
export type StaffPaymentTermsAcceptResponse = z.infer<
  typeof staffPaymentTermsAcceptResponseSchema
>;

export const staffStripeLinkResponseSchema = z.object({
  url: z.string().url(),
});
export type StaffStripeLinkResponse = z.infer<
  typeof staffStripeLinkResponseSchema
>;

// Public team-page presentation, independent of booking/schedule ordering.
export const workingSinceSchema = z.string().date();
export const teamPageMemberSchema = z.object({
  id: z.string(),
  identifier: z.string(),
  name: z.string(),
  photo: z.string().nullable(),
  bio: z.string().nullable(),
  workingSince: workingSinceSchema.nullable(),
  teamPageOrder: z.number().int().nonnegative().nullable(),
  tags: publicMemberTagsSchema.optional(),
  schedules: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      address: z.string().nullable(),
    }),
  ),
});
export type TeamPageMember = z.infer<typeof teamPageMemberSchema>;
export const teamPageResponseSchema = z.object({
  members: z.array(teamPageMemberSchema),
});
export const reorderTeamPageSchema = z
  .object({
    memberIds: z
      .array(z.string().min(1))
      .min(1)
      .max(1000)
      .refine(
        (ids) => new Set(ids).size === ids.length,
        "Each member must appear exactly once",
      ),
  })
  .strict();
